import { NavLink, Link } from "react-router";
import { useState, type ComponentType, type SVGProps } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn } from "@/lib/cn";
import { useMe } from "@/api/useMe";

/* ──────────────────────────────────────────────────────────────────────────
 * +CURA Studio sidebar
 *
 * Design references (the user has approved this direction):
 *   • Sana AI               – generous whitespace, right-aligned action icons,
 *                             quiet hover, single-pixel separators.
 *   • Paul Hanaoka         – two-part vertical layout: nav at top, account
 *                             pinned at the bottom.
 *   • Supabase             – dense without being shouty, sub-grouped sections.
 *
 * Composition:
 *   ┌───────────────────────┐
 *   │  [logo]            ‹   │  ← Brand block; collapse toggle top-right
 *   │  Studio Admin          │  ← role/context on its OWN line, bold
 *   │                        │
 *   │  ORDERING              │  ← eyebrow (10px, tracking-wider, fg-mute)
 *   │  ▸ icon  Order kits    │  ← active: studio-tint + 2px left accent
 *   │  ▸ icon  My designs    │
 *   │  ▸ icon  My orders     │
 *   │                        │
 *   │  BRAND                 │
 *   │  ▸ icon  Brand library │
 *   │                        │
 *   │  ADMIN                 │
 *   │  ▸ icon  Clients       │  ← uses admin tint when /admin/* is active
 *   │  …                     │
 *   └───────────────────────┘
 *
 * The logged-in user no longer lives here — it moved to a Google/Azure-style
 * account menu in the top-right (see layout/TopBar.tsx). The tenant-scope
 * switcher was removed at the user's request.
 *
 * Widths: 240px expanded, 64px icons-only. Icons inline-SVG (no new dep).
 * Tooltips on collapsed items via Radix (already in deps).
 * ──────────────────────────────────────────────────────────────────────── */

type IconProps = SVGProps<SVGSVGElement>;
type Icon = ComponentType<IconProps>;

// Lucide-derived 20px line icons. Inlined to avoid a new dep.
// stroke="currentColor", stroke-width 1.75 for a slightly lighter clinical feel.
const baseIconProps: IconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const PackageIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="m7.5 4.27 9 5.15" />
    <path d="M21 8 12 13 3 8" />
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="M12 22V13" />
  </svg>
);

const PaletteIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <circle cx="13.5" cy="6.5" r=".75" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r=".75" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r=".75" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r=".75" fill="currentColor" />
    <path d="M12 2a10 10 0 1 0 0 20 4 4 0 0 0 0-8h-1a3 3 0 1 1 0-6h7a8 8 0 0 0-6-6Z" />
  </svg>
);

const ClipboardListIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" />
    <path d="M12 16h4" />
    <path d="M8 11h.01" />
    <path d="M8 16h.01" />
  </svg>
);

const LibraryIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="m16 6 4 14" />
    <path d="M12 6v14" />
    <path d="M8 8v12" />
    <path d="M4 4v16" />
  </svg>
);

const UsersIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const BoxesIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5l-5-3-4.03 1.92Z" />
    <path d="m7 16.5-4.74-2.85" />
    <path d="m7 16.5 5-3" />
    <path d="M7 16.5v5.17" />
    <path d="M12 13.99V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 11l-5 2.99Z" />
    <path d="m17 16.5-5-3" />
    <path d="m17 16.5 4.74-2.85" />
    <path d="M17 16.5v5.17" />
    <path d="M7.5 4.27 12 7l4.5-2.73" />
    <path d="M7.5 4.27 12 7v5l-4.5-2.73a2 2 0 0 1-1-1.71V6a2 2 0 0 1 1-1.73Z" />
    <path d="M16.5 4.27 12 7v5l4.5-2.73a2 2 0 0 0 1-1.71V6a2 2 0 0 0-1-1.73Z" />
  </svg>
);

const ScrollTextIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4" />
    <path d="M19 17V5a2 2 0 0 0-2-2H4" />
    <path d="M15 8h-5" />
    <path d="M15 12h-5" />
  </svg>
);

const ChevronLeftIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const SettingsIcon: Icon = (p) => (
  <svg {...baseIconProps} {...p}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/* ── Nav data ────────────────────────────────────────────────────────── */

type NavItem = { to: string; label: string; icon: Icon; end?: boolean };
type NavGroup = {
  label: string;
  // "always" — visible to everyone signed in.
  // "admin" — visible only to STUDIO_ADMIN.
  // "tenantAdmin" — visible to CLIENT_ADMIN or STUDIO_ADMIN.
  show: "always" | "admin" | "tenantAdmin";
  items: NavItem[];
};

const groups: NavGroup[] = [
  {
    label: "Ordering",
    show: "always",
    items: [
      { to: "/catalogue", label: "Order kits", icon: PackageIcon },
      { to: "/designs", label: "My designs", icon: PaletteIcon },
      { to: "/orders", label: "My orders", icon: ClipboardListIcon },
    ],
  },
  {
    label: "Brand",
    show: "always",
    items: [{ to: "/brand-library", label: "Brand library", icon: LibraryIcon }],
  },
  {
    label: "Settings",
    show: "tenantAdmin",
    items: [{ to: "/settings/team", label: "Team", icon: SettingsIcon }],
  },
  {
    label: "Admin",
    show: "admin",
    items: [
      { to: "/admin/clients", label: "Clients", icon: UsersIcon },
      { to: "/admin/catalogue", label: "Catalogue", icon: BoxesIcon },
      { to: "/admin/orders", label: "Orders", icon: ClipboardListIcon },
      { to: "/admin/audit", label: "Audit log", icon: ScrollTextIcon },
    ],
  },
];

export function Sidebar({ isAdmin }: { isAdmin: boolean }) {
  const { data: me } = useMe();

  // Hide the Admin section unless the caller is actually a STUDIO_ADMIN.
  // Settings (Team) is visible to CLIENT_ADMINs on their own tenant too.
  // Until the API responds we err on the safe side (gated groups hidden).
  const isStudioAdmin = me?.user?.role === "STUDIO_ADMIN";
  const isTenantAdmin = isStudioAdmin || me?.user?.role === "CLIENT_ADMIN";
  const visible = groups.filter((g) => {
    if (g.show === "always") return true;
    if (g.show === "admin") return isStudioAdmin;
    if (g.show === "tenantAdmin") return isTenantAdmin;
    return false;
  });

  const [collapsed, setCollapsed] = useState(false);

  // Tint tokens shift with section; keep the rest of the chrome neutral.
  const accent = isAdmin ? "var(--admin)" : "var(--studio)";
  const accentTint = isAdmin ? "var(--admin-tint)" : "var(--studio-tint)";
  const accentDark = isAdmin ? "var(--admin-dark)" : "var(--studio-dark)";

  // Role/context label sits on its own bold line under the wordmark.
  const contextLabel = isAdmin ? "Studio Admin" : "Studio";

  return (
    <Tooltip.Provider delayDuration={120} skipDelayDuration={60}>
      <aside
        data-chrome
        className={cn(
          "flex flex-shrink-0 flex-col border-r border-[var(--border-1)] bg-white",
          "transition-[width] duration-150 ease-out",
          collapsed ? "w-16" : "w-60",
        )}
      >
        {/* Brand block. Logo wordmark + collapse toggle on the top row;
            the role/context label sits on its OWN bold line beneath the
            wordmark so "Studio Admin" is never trimmed. In collapsed mode
            only the logo shows (label hidden), with the expand toggle
            stacked below so the column stays centred. */}
        <div
          className={cn(
            "flex px-4 pt-5 pb-4",
            collapsed ? "flex-col items-center gap-2 px-2" : "flex-col gap-1.5",
          )}
        >
          <div
            className={cn(
              "flex w-full items-center gap-2",
              collapsed && "flex-col gap-2",
            )}
          >
            <Link
              to="/"
              className={cn(
                "flex min-w-0 items-center no-underline outline-none",
                "rounded focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
                !collapsed && "flex-1",
              )}
              aria-label="+CURA Studio home"
            >
              <span
                className={cn(
                  "flex items-center rounded-md",
                  collapsed ? "px-1.5 py-1" : "px-2 py-1.5",
                )}
                style={{ background: "var(--brand-primary)" }}
              >
                <img
                  src="/cura-logo.svg"
                  alt="+CURA"
                  className={cn("block object-contain", collapsed ? "h-4" : "h-3.5")}
                />
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className={cn(
                "grid h-7 w-7 flex-shrink-0 place-items-center rounded text-fg-3",
                "hover:bg-bg-1 hover:text-fg-1",
                "outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
              )}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <ChevronRightIcon className="h-3.5 w-3.5" />
              ) : (
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          {!collapsed && (
            <span
              className="text-[11px] font-bold uppercase tracking-[0.16em]"
              style={{ color: accentDark }}
            >
              {contextLabel}
            </span>
          )}
        </div>

        {/* Nav groups — fill remaining space, scroll if needed */}
        <nav
          className={cn(
            "flex flex-1 flex-col gap-5 overflow-y-auto border-t border-[var(--border-1)]",
            collapsed ? "px-2 py-3" : "px-3 py-4",
          )}
          aria-label="Primary"
        >
          {visible.map((group) => (
            <div key={group.label}>
              {!collapsed && (
                <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-mute">
                  {group.label}
                </div>
              )}
              <ul className="flex list-none flex-col gap-0.5 p-0">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SidebarItem
                      item={item}
                      collapsed={collapsed}
                      accent={accent}
                      accentTint={accentTint}
                      accentDark={accentDark}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* The Admin section in the nav already gates on role; the old
              "Studio Admin / Back to Studio" surface toggle was a demo
              hangover — gone. Admin users navigate via the Admin nav group;
              clicking the +CURA logo always lands on /catalogue. */}
        </nav>
      </aside>
    </Tooltip.Provider>
  );
}

/* ── Single sidebar row ──────────────────────────────────────────────── */

function SidebarItem({
  item,
  collapsed,
  accent,
  accentTint,
  accentDark,
  variant = "primary",
  forceInactive = false,
}: {
  item: NavItem;
  collapsed: boolean;
  accent: string;
  accentTint: string;
  accentDark: string;
  variant?: "primary" | "ghost";
  forceInactive?: boolean;
}) {
  const Icon = item.icon;

  const row = (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => {
        const active = !forceInactive && isActive;
        return cn(
          "group relative flex items-center rounded-md text-[13px] font-medium no-underline",
          "outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
          "transition-colors duration-100",
          collapsed
            ? // Consistent 40px square, icon dead-centre, full hit area.
              "mx-auto h-10 w-10 justify-center"
            : "h-9 gap-2.5 pl-3 pr-2",
          active
            ? // Strong-but-quiet active state: tinted bg + 2px left accent
              "text-[color:var(--active-fg)] bg-[color:var(--active-bg)]"
            : variant === "ghost"
              ? "text-fg-3 hover:bg-bg-1 hover:text-fg-1"
              : "text-fg-2 hover:bg-bg-1 hover:text-fg-1",
        );
      }}
      style={
        {
          // Custom-properties consumed only when className picks `active`.
          // Harmless when not active — no styles read them.
          ["--active-bg" as never]: accentTint,
          ["--active-fg" as never]: accentDark,
        } as React.CSSProperties
      }
    >
      {({ isActive }) => {
        const active = !forceInactive && isActive;
        return (
          <>
            {/* Active accent edge. Expanded: a 2px rule on the sidebar's
                left gutter. Collapsed: a short centred bar hugging the
                square's left side so the active state still reads at
                icon size without clipping the rounded corners. */}
            <span
              aria-hidden
              className={cn(
                "absolute left-0 w-[2px] rounded-r-sm transition-opacity",
                collapsed ? "top-2 bottom-2" : "top-1.5 bottom-1.5",
                active ? "opacity-100" : "opacity-0",
              )}
              style={{ background: accent }}
            />
            <Icon
              className={cn(
                "flex-shrink-0",
                collapsed ? "h-[18px] w-[18px]" : "h-[17px] w-[17px]",
              )}
            />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </>
        );
      }}
    </NavLink>
  );

  if (!collapsed) return row;

  // Collapsed: wrap in Radix Tooltip so the label is still discoverable
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{row}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={8}
          className="rounded-md bg-fg-1 px-2 py-1 text-[11px] font-medium text-white shadow-md"
        >
          {item.label}
          <Tooltip.Arrow className="fill-[var(--fg-1)]" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/* The tenant-scope switcher and the in-sidebar account block were both
 * removed at the user's request. Tenant scoping plumbing (the
 * `x-active-tenant` header in api/client.ts) is intentionally left intact;
 * only the sidebar UI affordance is gone. The logged-in user now lives in
 * the top-right account menu — see layout/TopBar.tsx. */
